import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Sparkles, Brain, Flame, Heart, ArrowRight, Sun, Moon } from "lucide-react";
import { Button } from "../components/ui/button";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function LandingPage() {
  const { openAuthModal, user, loading } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      window.location.href = user.has_completed_assessment ? '/maya-manthan' : '/questionnaire';
    } else {
      openAuthModal();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F1A] to-[#1A1F3A] relative overflow-hidden">
      {/* Ambient background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-sacred-purple/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-sacred-gold/5 rounded-full blur-3xl" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sacred-gold/20 to-transparent" />
      </div>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-6" data-testid="nav-bar">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sacred-gold/20 to-sacred-gold/5 flex items-center justify-center border border-sacred-gold/20">
              <Sun className="w-5 h-5 text-sacred-gold" />
            </div>
            <span className="font-heading text-2xl text-sacred-gold tracking-tight">Maya Manthan</span>
          </div>
          <div className="flex items-center gap-4">
            {!loading && user ? (
              <Button 
                onClick={() => window.location.href = '/maya-manthan'} 
                className="bg-gradient-to-r from-sacred-gold to-sacred-gold-light text-sacred-text-on-gold font-body font-medium px-6 py-2.5 rounded-full hover:shadow-gold-glow transition-all duration-300"
                data-testid="nav-dashboard-btn"
              >
                Enter Dashboard
              </Button>
            ) : (
              <Button 
                onClick={openAuthModal} 
                className="bg-white/5 backdrop-blur-xl border border-white/10 text-sacred-text-primary font-body px-6 py-2.5 rounded-full hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                data-testid="nav-login-btn"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <motion.section 
        className="relative pt-40 pb-32 px-6 md:px-12"
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        <div className="max-w-5xl mx-auto text-center">
          <motion.div variants={fadeInUp}>
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sacred-card-glass backdrop-blur-xl border border-white/10 text-sacred-text-secondary text-sm mb-8 font-body">
              <Sparkles className="w-4 h-4 text-sacred-gold" />
              Inner Transformation Platform
            </span>
          </motion.div>
          
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl text-sacred-text-primary leading-[1.1] mb-8"
          >
            Discover Your Path to<br />
            <span className="bg-gradient-to-r from-sacred-gold to-sacred-gold-light bg-clip-text text-transparent">Inner Awakening</span>
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-lg md:text-xl text-sacred-text-secondary max-w-2xl mx-auto mb-12 font-body leading-relaxed"
          >
            Assess your chakra balance, understand your mind patterns, and follow a personalized sadhana to transform your inner world.
          </motion.p>
          
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleGetStarted}
              className="group bg-gradient-to-r from-sacred-gold to-sacred-gold-light text-sacred-text-on-gold font-body font-medium px-8 py-4 rounded-full text-lg hover:shadow-gold-glow-lg transition-all duration-300"
              data-testid="hero-cta-btn"
            >
              Begin My Journey
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white/5 backdrop-blur-xl border border-white/10 text-sacred-text-primary font-body px-8 py-4 rounded-full text-lg hover:bg-white/10 transition-all duration-300"
            >
              Learn More
            </Button>
          </motion.div>
        </div>
      </motion.section>

      {/* Features Section */}
      <section id="features" className="relative py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-sacred-text-primary mb-4">
              Your Sacred Journey Awaits
            </h2>
            <p className="text-sacred-text-secondary font-body max-w-xl mx-auto">
              A comprehensive system for inner transformation, guided by ancient wisdom and modern insights.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Brain,
                title: "Deep Assessment",
                description: "Understand your chakra imbalances, mind patterns (Guna), and emotional tendencies through AI-powered analysis.",
                color: "sacred-purple"
              },
              {
                icon: Flame,
                title: "Personalized Sadhana",
                description: "Receive daily practices tailored to your inner state — meditation, breathwork, and Diya Tratak using ghee lamp.",
                color: "sacred-gold"
              },
              {
                icon: Heart,
                title: "Transformation Path",
                description: "Progress through 5 levels of inner growth, from stabilizing the mind to experiencing profound stillness.",
                color: "sacred-blue"
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.7 }}
                className="sacred-card p-8 hover:border-white/15 transition-all duration-300 group"
              >
                <div className={`w-14 h-14 rounded-2xl bg-${feature.color}/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                  style={{ backgroundColor: feature.color === 'sacred-gold' ? 'rgba(212, 175, 55, 0.1)' : feature.color === 'sacred-purple' ? 'rgba(168, 85, 247, 0.1)' : 'rgba(59, 130, 246, 0.1)' }}
                >
                  <feature.icon className="w-7 h-7" style={{ color: feature.color === 'sacred-gold' ? '#D4AF37' : feature.color === 'sacred-purple' ? '#A855F7' : '#3B82F6' }} />
                </div>
                <h3 className="font-heading text-xl text-sacred-text-primary mb-3">{feature.title}</h3>
                <p className="text-sacred-text-secondary font-body text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Preview */}
      <section className="relative py-24 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-sacred-text-primary mb-4">
              Sacred Courses
            </h2>
            <p className="text-sacred-text-secondary font-body max-w-xl mx-auto">
              Deep-dive programs for serious practitioners ready to transform.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "Diya Tratak", price: "₹1,500/mo", desc: "Steady gaze meditation with ghee lamp" },
              { name: "Chakra Balancing", price: "₹3,000/mo", desc: "3-month chakra healing journey" },
              { name: "Tridevi Sadhana", price: "₹4,000/mo", desc: "4-month divine feminine practice" },
              { name: "Ganesh Sadhana", price: "₹2,000/mo", desc: "3-month obstacle removal practice" }
            ].map((course, index) => (
              <motion.div
                key={course.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.7 }}
                className="bg-[#12182B]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 hover:border-sacred-gold/30 transition-all duration-300 group"
              >
                <h3 className="font-heading text-lg text-sacred-text-primary mb-2">{course.name}</h3>
                <p className="text-sacred-gold font-body font-medium mb-3">{course.price}</p>
                <p className="text-sacred-text-muted text-sm font-body">{course.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6 md:px-12">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto bg-gradient-to-br from-[#12182B] to-[#1A1F3A] border border-sacred-gold/20 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-sacred-gold/5 rounded-3xl blur-3xl" />
          <div className="relative z-10">
            <Moon className="w-12 h-12 text-sacred-gold mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl text-sacred-text-primary mb-4">
              Ready to Begin Your Journey?
            </h2>
            <p className="text-sacred-text-secondary font-body max-w-lg mx-auto mb-8">
              Take the first step towards inner transformation. Your sacred path awaits.
            </p>
            <Button 
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-sacred-gold to-sacred-gold-light text-sacred-text-on-gold font-body font-medium px-10 py-4 rounded-full text-lg hover:shadow-gold-glow-lg transition-all duration-300"
              data-testid="cta-btn"
            >
              Start Free Assessment
            </Button>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6 md:px-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sun className="w-6 h-6 text-sacred-gold" />
            <span className="font-heading text-xl text-sacred-gold">Maya Manthan</span>
          </div>
          <p className="text-sacred-text-muted text-sm font-body">
            © 2026 Maya Manthan. Inner Transformation Platform.
          </p>
          <div className="flex items-center gap-6">
            <a href="https://www.youtube.com/@MayaManthan555" target="_blank" rel="noopener noreferrer" className="text-sacred-text-muted hover:text-sacred-gold transition-colors text-sm font-body">
              YouTube
            </a>
            <a href="#" className="text-sacred-text-muted hover:text-sacred-gold transition-colors text-sm font-body">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
